import React, { useState } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'
import { X, Tag, Loader2, CalendarIcon } from 'lucide-react'
import { format } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { useAuthStore } from '@/store/authStore'
import { taskService } from '@/services/taskService'
import { sprintService } from '@/services/sprintService'

const userName = (u) => (u.employee ? `${u.employee.first_name} ${u.employee.last_name || ''}`.trim() : u.email)

// Sticky bar for changing many selected tasks at once. Every task gets its own history,
// notifications and Jira sync on the server; ones that can't take a change are reported.
const BulkActionBar = ({ projectId, selectedIds, statuses, users, onClear }) => {
  const queryClient = useQueryClient()
  const { user } = useAuthStore()
  const [busy, setBusy] = useState(false)
  const [labelText, setLabelText] = useState('')
  const count = selectedIds.length
  const { data: sprintData } = useQuery(['projects', String(projectId), 'sprints'], () => sprintService.getSprints(projectId), { enabled: count > 0 })
  const sprints = sprintData?.data?.sprints || []

  const apply = async (set, what) => {
    setBusy(true)
    try {
      const { data } = await taskService.bulkUpdate(projectId, selectedIds, set)
      const changed = data.updated.filter(u => u.changed.length).length
      if (data.failed.length) {
        toast.warning(`${what}: ${changed} updated, ${data.failed.length} couldn't be changed`, {
          description: data.failed.slice(0, 5).map(f => `${f.key}: ${f.error}`).join('\n')
        })
      } else {
        toast.success(`${what}: ${changed} of ${count} ${count === 1 ? 'task' : 'tasks'} changed`)
      }
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      queryClient.invalidateQueries(['projects', String(projectId), 'backlog'])
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bulk change failed')
    } finally {
      setBusy(false)
    }
  }

  const labels = (key) => {
    const value = labelText.trim()
    if (!value) return
    apply({ [key]: value.split(',').map(s => s.trim()).filter(Boolean) }, key === 'labels_add' ? 'Labels added' : 'Labels removed')
    setLabelText('')
  }

  if (!count) return null

  return (
    <div className="sticky bottom-4 z-20 mx-auto flex w-fit max-w-full flex-wrap items-center gap-2 rounded-lg border bg-background px-3 py-2 shadow-lg" role="toolbar" aria-label="Bulk actions">
      <span className="text-sm font-medium whitespace-nowrap">{count} selected</span>
      {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}

      <Select value="" onValueChange={(v) => apply({ status_id: Number(v) }, 'Status')} disabled={busy}>
        <SelectTrigger className="h-8 w-[130px] text-xs" aria-label="Set status"><SelectValue placeholder="Status…" /></SelectTrigger>
        <SelectContent>
          {statuses.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value="" onValueChange={(v) => apply({ assignee_id: v === 'none' ? null : Number(v) }, 'Assignee')} disabled={busy}>
        <SelectTrigger className="h-8 w-[140px] text-xs" aria-label="Set assignee"><SelectValue placeholder="Assignee…" /></SelectTrigger>
        <SelectContent>
          {user && <SelectItem value={String(user.id)}>Me</SelectItem>}
          <SelectItem value="none">Unassigned</SelectItem>
          {users.filter(u => u.id !== user?.id).map(u => <SelectItem key={u.id} value={String(u.id)}>{userName(u)}</SelectItem>)}
        </SelectContent>
      </Select>

      {sprints.length > 0 && (
        <Select value="" onValueChange={(v) => apply({ sprint_id: v === 'backlog' ? null : Number(v) }, 'Sprint')} disabled={busy}>
          <SelectTrigger className="h-8 w-[130px] text-xs" aria-label="Move to sprint"><SelectValue placeholder="Sprint…" /></SelectTrigger>
          <SelectContent>
            {sprints.map(s => <SelectItem key={s.id} value={String(s.id)}>{s.name}{s.state === 'active' ? ' (active)' : ''}</SelectItem>)}
            <SelectItem value="backlog">Backlog</SelectItem>
          </SelectContent>
        </Select>
      )}

      <Select value="" onValueChange={(v) => apply({ priority: v }, 'Priority')} disabled={busy}>
        <SelectTrigger className="h-8 w-[115px] text-xs" aria-label="Set priority"><SelectValue placeholder="Priority…" /></SelectTrigger>
        <SelectContent>
          {['low', 'medium', 'high', 'critical'].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
        </SelectContent>
      </Select>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy}><CalendarIcon className="h-3.5 w-3.5 mr-1" /> Due…</Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" onSelect={(date) => date && apply({ due_date: format(date, 'yyyy-MM-dd') }, 'Due date')} initialFocus />
          <div className="border-t p-2">
            <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => apply({ due_date: null }, 'Due date cleared')}>Clear due date</Button>
          </div>
        </PopoverContent>
      </Popover>

      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 text-xs" disabled={busy}><Tag className="h-3.5 w-3.5 mr-1" /> Labels…</Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 space-y-2" align="start">
          <Input value={labelText} onChange={(e) => setLabelText(e.target.value)} placeholder="backend, q4-release" className="h-8 text-sm" />
          <div className="flex gap-2">
            <Button size="sm" className="flex-1 h-8" onClick={() => labels('labels_add')}>Add</Button>
            <Button size="sm" variant="outline" className="flex-1 h-8" onClick={() => labels('labels_remove')}>Remove</Button>
          </div>
        </PopoverContent>
      </Popover>

      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClear} title="Clear selection">
        <X className="h-4 w-4" />
      </Button>
    </div>
  )
}

export default BulkActionBar
